<template>
  <div>
    <page-header class="mt-3" :page="page" />
    <FilterManager
      ref="filterComponent"
      v-model="filtersSelected"
      class="mb-4 c-filterManager"
      :filters="filtersConfig"
      @search="search(1)"
    />
    <separator
      v-if="pagination.totalItemCount > 0"
      class="my-3"
      text="Resultados de Espacios Disponibles"
    />

    <row
      v-for="(result, index) in pagination.data"
      :key="result.clientAppointmentSpaceID"
      ref="rowRef"
      class="mb-3"
      :index="index"
      :value="result"
      @close-all-details="closeAllDetails"
      @create-space="onCreateSpaceHandler"
      @delete-space="onDeleteSpaceHandler"
      @edit-space="onEditSpaceHandler"
    />

    <not-available-space
      v-if="pagination.totalItemCount === 0"
      class="mt-2"
      message="No existen espacios disponibles."
    />

    <pagination
      v-if="pagination.totalItemCount > 15"
      :data="pagination"
      @pagination-go-page="setCurrentPage"
    />
  </div>
</template>
<script>
import moment from 'moment';
import 'moment/locale/es-do';
import { mapState } from 'vuex';

import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import Pagination from 'pagination';
import Row from './Row';
import { NotAvailableSpace } from './components';
import alertMixin from '@/mixins/alert.mixin';
import storeMixin from 'utils/mixins/storeGenerator';

import { getAvailableMonths as _getAvailableMonths } from '@/modules/shared/academicyear.api';

import { getProvidersByAcademicyearId as _getProvidersByAcademicyearId } from '@/modules/shared/provider.api';

import {
  initAvailableSpacesFilters as _initAvailableSpacesFilters,
  getServiceLocations as _getServiceLocations,
  getSpecialists as _getSpecialists,
  getServiceTypes as _getServiceTypes,
  getServices as _getServices,
  searchAvailableSpaces as _searchAvailableSpaces,
  deleteAvailableSpace as _deleteAvailableSpace,
  canCreateAvailableSpace as _canCreateAvailableSpace,
  getMunicipalities as _getMunicipalities,
} from '@/modules/shared/providerappointment.api';

import roles from '@/utils/constants/roles.js';

const CREATE_AVAILABLE_SPACE_ROUTE_NAME = 'SpaceManagement';
const STORE_MODULE_NAME = 'AvailableSpacesModule';

export default {
  name: 'SpacesAvailable',
  components: {
    PageHeader,
    Pagination,
    Row,
    NotAvailableSpace,
    FilterManager,
  },
  mixins: [alertMixin, storeMixin],
  data: () => ({
    moduleName: STORE_MODULE_NAME,
    page: {
      icon: 'fas fa-user-circle',
      title: 'Espacios Disponibles',
      routes: ['Proveedor', 'Manejo de Referido', 'Espacios Disponibles'],
    },
    filters: {
      municipalities: [],
      locations: [],
      specialists: [],

      academicYears: [],
      months: [],
      companies: [],
      servicetypes: [],
      disciplineTypes: [],
    },
    filtersSelected: {
      municipality: null,
      location: null,
      specialist: null,
      academicYear: null,
      month: null,
      company: null,
      serviceType: null,
      disciplineType: null,
      onlyAvailableSpaces: null,
    },
    canFilterByYear: false,
    results: [],
    pagination: {
      totalItemCount: 0,
      count: 15,
      pageCount: 0,
      currentPage: 1,
      data: null,
    },
    pageSize: 15,
    radioButtonsOptions: {
      options1: [
        { label: 'Ver Sólo Espacios Disponibles', value: 0 },
        { label: 'Ver Todos', value: 1 },
      ],
    },
  }),
  computed: {
    ...mapState('$_user', ['user', 'companies']),
    isProvider() {
      return [roles.Provider].includes(this.userRole);
    },
    isAssistantProvider() {
      return [roles.AsistenteProveedor].includes(this.userRole);
    },
    isSpecialist() {
      return [roles.Especialista].includes(this.userRole);
    },
    adminFilterConfig() {
      return [
        {
          name: 'company',
          component: 'CustomDropDown',
          options: this.filters.companies,
          disabled: this.filters.companies === 1,
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: false,
          class: 'col-sm-12 col-md-4',
          placeholder: 'Seleccione',
          onChanged: this.onCompanyChanged,
          clearModelsOnUpdate: [],
          clearable: true,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.filters.municipalities,
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: false,
          class: 'col-sm-12 col-md-4',
          placeholder: 'Seleccione Pueblo',
          disabled:
            !this.filtersSelected.company ||
            this.filters.municipalities.length === 1,
          onChanged: this.onMunicipalityChanged,
          clearModelsOnUpdate: [],
        },
        {
          name: 'location',
          component: 'CustomDropDown',
          options: this.filters.locations,
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: false,
          class: 'col-sm-12 col-md-4',
          placeholder: 'Seleccione Localización',
          onChanged: this.onLocationChanged,
          disabled:
            !this.filtersSelected.municipality ||
            this.filters.locations?.length === 1,
          clearModelsOnUpdate: [],
        },
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          initialValue: this.currentAcademicYear(),
          options: this.filters.academicYears,
          fieldName: 'description',
          fieldKey: 'id',
          label: 'Año Fiscal:',
          isAdvanced: true,
          class: 'col-sm-12 col-md-3',
          placeholder: 'Seleccione',
          onChanged: this.onAcademicYearChanged,
          visible: this.canFilterByYear,
          clearable: false,
          clearModelsOnUpdate: [],
        },
        {
          name: 'month',
          component: 'CustomDropDown',
          options: this.filters.months,
          fieldName: 'name',
          fieldKey: 'id',
          label: 'Mes:',
          isAdvanced: true,
          class: 'col-sm-12 col-md-3 offset-md-1',
          placeholder: 'Seleccione',
          visible: this.canFilterByYear,
          disabled: ['academicYear'],
          clearModelsOnUpdate: [],
        },
        {
          name: 'specialist',
          component: 'CustomDropDown',
          options: this.filters.specialists,
          fieldName: 'name',
          fieldKey: 'id',
          label: 'Especialista:',
          isAdvanced: true,
          class: 'col-sm-12 col-md-3 offset-md-1',
          placeholder: 'Seleccione Especialista',
          disabled: (this.isSpecialist &&
            this.filters.specialists &&
            this.filters.specialists.length === 1) || ['company'],
          onChanged: this.onSpecialistChanged,
          clearModelsOnUpdate: [],
        },
        {
          name: 'serviceType',
          component: 'CustomDropDown',
          options: this.filters.serviceTypes,
          fieldName: 'name',
          fieldKey: 'id',
          label: 'Tipo de Servicio:',
          isAdvanced: true,
          class: this.canFilterByYear
            ? 'col-sm-12 col-md-3'
            : 'col-sm-12 col-md-3 offset-md-1',
          placeholder: 'Seleccione',
          onChanged: this.onServiceTypeChanged,
          clearModelsOnUpdate: [],
        },
        {
          name: 'disciplineType',
          component: 'CustomDropDown',
          options: this.filters.disciplineTypes,
          fieldName: 'name',
          fieldKey: 'id',
          label: 'Servicio:',
          isAdvanced: true,
          class: this.canFilterByYear
            ? 'col-sm-12 col-md-3 offset-md-1'
            : 'col-sm-12 col-md-3 offset-md-1',
          placeholder: 'Seleccione',
          clearModelsOnUpdate: [],
        },
        {
          name: 'filterOption', //0 - View Available, 1 - View All
          componentName: 'onlyAvailableSpaces',
          component: 'CustomRadioGroup',
          options: this.radioButtonsOptions.options1,
          fieldKey: 'value',
          isAdvanced: true,
          class: this.canFilterByYear
            ? 'col-sm-12 col-md-3 offset-md-1 pt-28 pl-0 pr-0'
            : 'col-sm-12 col-md-4 pt-28 pl-0 pr-0',
          isInline: true,
        },
      ];
    },
    providerFilterConfig() {
      return [
        {
          name: 'company',
          component: 'CustomDropDown',
          options: this.filters.companies,
          disabled: this.filters.companies === 1,
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: false,
          class: 'col-sm-12 col-md-4',
          placeholder: 'Seleccione',
          onChanged: this.onCompanyChanged,
          clearModelsOnUpdate: [],
          clearable: true,
          visible: true,
          validations: 'required',
          error: 'Campo Requerido.',
        },
        {
          name: 'specialist',
          component: 'CustomDropDown',
          options: this.filters.specialists,
          fieldName: 'name',
          fieldKey: 'id',
          //label: 'Especialista:',
          isAdvanced: false,
          class: 'col-sm-12 col-md-4',
          placeholder: 'Seleccione Especialista',
          disabled: (this.isSpecialist &&
            this.filters.specialists.length === 1) || ['company'],
          onChanged: this.onSpecialistChanged,
          clearModelsOnUpdate: [],
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.filters.municipalities,
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: false,
          class: 'col-sm-12 col-md-4',
          placeholder: 'Seleccione Pueblo',
          disabled:
            !this.filtersSelected.company ||
            this.filters.municipalities.length === 1,
          onChanged: this.onMunicipalityChanged,
          clearModelsOnUpdate: [],
        },
        {
          name: 'location',
          component: 'CustomDropDown',
          options: this.filters.locations,
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: false,
          class: 'col-sm-12 col-md-4',
          placeholder: 'Seleccione Localización',
          onChanged: this.onLocationChanged,
          disabled:
            !this.filtersSelected.municipality ||
            this.filters.locations?.length === 1,
          clearModelsOnUpdate: [],
        },
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          initialValue: this.currentAcademicYear(),
          options: this.filters.academicYears,
          fieldName: 'description',
          fieldKey: 'id',
          label: 'Año Fiscal:',
          isAdvanced: true,
          class: 'col-sm-12 col-md-4',
          placeholder: 'Seleccione',
          onChanged: this.onAcademicYearChanged,
          visible: this.canFilterByYear,
          clearable: false,
          clearModelsOnUpdate: [],
        },
        {
          name: 'month',
          component: 'CustomDropDown',
          options: this.filters.months,
          fieldName: 'name',
          fieldKey: 'id',
          label: 'Mes:',
          isAdvanced: true,
          class: 'col-sm-12 col-md-4',
          placeholder: 'Seleccione',
          visible: this.canFilterByYear,
          disabled: ['academicYear'],
          clearModelsOnUpdate: [],
        },
        {
          name: 'serviceType',
          component: 'CustomDropDown',
          options: this.filters.serviceTypes,
          fieldName: 'name',
          fieldKey: 'id',
          label: 'Tipo de Servicio:',
          isAdvanced: true,
          class: this.canFilterByYear
            ? 'col-sm-12 col-md-4'
            : 'col-sm-12 col-md-4',
          placeholder: 'Seleccione',
          onChanged: this.onServiceTypeChanged,
          clearModelsOnUpdate: [],
        },
        {
          name: 'disciplineType',
          component: 'CustomDropDown',
          options: this.filters.disciplineTypes,
          fieldName: 'name',
          fieldKey: 'id',
          label: 'Servicio:',
          isAdvanced: true,
          class: this.canFilterByYear
            ? 'col-sm-12 col-md-4'
            : 'col-sm-12 col-md-4',
          placeholder: 'Seleccione',
          clearModelsOnUpdate: [],
        },
        {
          name: 'filterOption', //0 - View Available, 1 - View All
          componentName: 'onlyAvailableSpaces',
          component: 'CustomRadioGroup',
          options: this.radioButtonsOptions.options1,
          fieldKey: 'value',
          isAdvanced: true,
          class: this.canFilterByYear
            ? 'col-sm-12 col-md-4  pt-28 pl-0 pr-0'
            : 'col-sm-12 col-md-4 pt-28 pl-0 pr-0',
          isInline: true,
        },
      ];
    },
    filtersConfig() {
      if (this.isProvider || this.isAssistantProvider) {
        return this.providerFilterConfig;
      }

      return this.adminFilterConfig;
    },
  },
  watch: {
    'filters.specialists'(newValue) {
      if (newValue && newValue.length === 1) {
        this.filtersSelected.specialist = newValue[0];
      }
    },
    'filtersSelected.location'(newValue) {
      if (newValue != null && new newValue() == null) {
        this.filters.companies = null;
        this.filtersSelected.company = null;
      }
    },
  },
  async created() {
    await this.initFilters();

    await this.recoveryData();
    await this.recoverLastRowOpened();
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async initFilters() {
      const { data } = await _initAvailableSpacesFilters();

      this.filters = data;
      if (this.filters.companies.length == 1) {
        this.filtersSelected.company = this.filters.companies[0];
        await this.onCompanyChanged();
      }

      this.canFilterByYear = data.canCreateSpaceForNextYear;
    },
    async recoverLastRowOpened() {
      const results = this.pagination.data;

      if (results && results.length > 0) {
        const currentOpened = results.find((x) => x.isOpen);
        await this.search(this.pagination.currentPage);

        if (currentOpened) {
          const index = this.pagination.data.findIndex(
            (x) => x.specialistId === currentOpened.specialistId
          );
          if (index !== -1) this.pagination.data[index].isOpen = true;
        }
      }
    },
    closeAllDetails(currentRow) {
      for (let i = 0; i < this.pagination.data.length; i++)
        if (this.pagination.data[i] != currentRow)
          this.pagination.data[i].isOpen = false;

      this.$forceUpdate();
    },
    setCurrentPage(pageNumber) {
      this.search(pageNumber);
    },
    onMunicipalityChanged() {
      this.filtersSelected.location = null;

      if (this.filtersSelected.municipality != null) {
        _getServiceLocations(
          this.filtersSelected.company != null
            ? this.filtersSelected.company.id
            : null,
          this.filtersSelected.municipality != null
            ? this.filtersSelected.municipality.id
            : null,
          this.filtersSelected.specialist != null
            ? this.filtersSelected.specialist.id
            : null
        ).then(({ data }) => {
          this.filters.locations = data;
          if (data.length == 1) {
            this.filtersSelected.location = data[0];
          }
        });
      }

      this.fetchServiceTypes();
      this.fetchServices();
    },
    onAcademicYearChanged() {
      _getAvailableMonths(this.filtersSelected.academicYear.id).then(
        ({ data }) => (this.filters.months = data)
      );

      if (!this.isProvider && !this.isSpecialist) {
        _getProvidersByAcademicyearId(
          this.filtersSelected.academicYear.id
        ).then(({ data }) => (this.filters.companies = data));
      } else {
        this.filters.companies = this.companies;
      }
    },
    async onLocationChanged() {
      this.fetchServiceTypes();
      this.fetchServices();
    },
    onServiceTypeChanged() {
      this.fetchServices();
    },
    async onSpecialistChanged() {
      if (
        this.filtersSelected.specialist != null &&
        (this.isProvider || this.isAssistantProvider)
      ) {
        this.filtersSelected.municipality = null;
        this.filtersSelected.location = null;

        await _getMunicipalities(
          this.filtersSelected.company != null
            ? this.filtersSelected.company.id
            : null,
          this.filtersSelected.specialist != null
            ? this.filtersSelected.specialist.id
            : null
        ).then(({ data }) => {
          this.filters.municipalities = data;

          if (data.length == 1) {
            this.filtersSelected.municipality = data[0];
            this.onMunicipalityChanged();
          }
        });
      }

      this.fetchServiceTypes();
      this.fetchServices();
    },
    async onCompanyChanged() {
      this.filtersSelected.municipality = null;
      this.filtersSelected.location = null;
      this.filtersSelected.specialist = null;

      if (this.filtersSelected.company == null) {
        return;
      }

      this.fetchServiceTypes();
      this.fetchServices();

      await _getSpecialists(this.filtersSelected.company.id, null, null).then(
        ({ data }) => (this.filters.specialists = data)
      );
      await _getMunicipalities(
        this.filtersSelected.company != null
          ? this.filtersSelected.company.id
          : null,
        this.filtersSelected.specialist != null
          ? this.filtersSelected.specialist.id
          : null
      ).then(({ data }) => {
        this.filters.municipalities = data;

        if (data.length == 1) {
          this.filtersSelected.municipality = data[0];
          this.onMunicipalityChanged();
        }
      });
    },
    async fetchServiceTypes() {
      await _getServiceTypes(
        this.filtersSelected.company != null
          ? this.filtersSelected.company.id
          : null,
        this.filtersSelected.specialist != null
          ? this.filtersSelected.specialist.id
          : null,
        this.filtersSelected.municipality != null
          ? this.filtersSelected.municipality.id
          : null,
        this.filtersSelected.location != null
          ? this.filtersSelected.location.id
          : null
      ).then(async ({ data }) => {
        this.filters.serviceTypes = data;
      });
    },
    async fetchServices() {
      this.filters.disciplineTypes = [];

      await _getServices(
        this.filtersSelected.location != null
          ? this.filtersSelected.location.id
          : null,
        this.filtersSelected.serviceType != null
          ? this.filtersSelected.serviceType.id
          : null,
        this.filtersSelected.company != null
          ? this.filtersSelected.company.id
          : null,
        this.filtersSelected.specialist != null
          ? this.filtersSelected.specialist.id
          : null,
        this.filtersSelected.municipality != null
          ? this.filtersSelected.municipality.id
          : null
      ).then(async ({ data }) => {
        this.filters.disciplineTypes = data;
      });
    },
    async search(pageNumber = 1) {
      if (this.isProvider || this.isAssistantProvider) {
        var validate = await this.$refs['filterComponent'].$refs[
          'formValidator'
        ].validate();
        if (!validate) {
          return;
        }
      }
      let model = {
        municipalityId: this.filtersSelected.municipality?.id,
        locationId: this.filtersSelected.location?.id,
        specialistId: this.filtersSelected.specialist?.id,
        fiscalYearId: this.filtersSelected.academicYear?.id,
        month: this.filtersSelected.month?.id,
        companyId: this.filtersSelected.company?.id,
        serviceTypeId: this.filtersSelected.serviceType?.id,
        serviceId: this.filtersSelected.disciplineType?.id,
        onlyAvailableSpaces: this.filtersSelected.filterOption == 0,
        page: pageNumber,
        pageSize: this.pageSize,
      };

      await _searchAvailableSpaces(model).then(({ data }) => {
        for (let i = 0; i < data.data.length; i++) data.data[i].isOpen = false;

        this.pagination = data;
      });
    },
    async onCreateSpaceHandler({
      fiscalYearId,
      specialistId,
      specialistName,
      socialSecurityNo,
    }) {
      await _canCreateAvailableSpace()
        .then(() => {
          this.$router.push({
            name: CREATE_AVAILABLE_SPACE_ROUTE_NAME,
            params: {
              fiscalYearId,
              specialistId,
              specialistInfo: {
                specialistName,
                socialSecurityNo,
                specialistId,
              },
            },
          });
        })
        .catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });
    },
    async onDeleteSpaceHandler(clientAppointmentSpaceID, parentIndex) {
      if (clientAppointmentSpaceID) {
        const answer = await this.ShowCancelModal(
          'Confirmación',
          '¿Está seguro que desea eliminar el espacio disponible?',
          'Sí',
          'No'
        );
        if (answer) {
          await _deleteAvailableSpace(clientAppointmentSpaceID);
          this.ShowToast(
            'Proceso Exitoso',
            'Espacio eliminado exitosamente.',
            'success'
          );
          this.removeSpace(parentIndex, clientAppointmentSpaceID);
        }
      }
    },
    async removeSpace(parentIndex, clientAppointmentSpaceID) {
      this.pagination = {
        ...this.pagination,
        data: this.pagination.data.map((pItem, pIndex) => {
          if (pIndex === parentIndex) {
            pItem.isOpen = false;
            pItem.appointmentDetails = pItem.appointmentDetails.filter(
              (cItem) =>
                cItem.clientAppointmentSpaceID != clientAppointmentSpaceID
            );
          }
          return pItem;
        }),
      };
      setTimeout(() => {
        this.$refs.rowRef[parentIndex].showDetail();
      }, 0);
    },
    onEditSpaceHandler(
      { fiscalYearId, specialistId, specialistName, socialSecurityNo },
      item
    ) {
      this.$router.push({
        name: CREATE_AVAILABLE_SPACE_ROUTE_NAME,
        params: {
          fiscalYearId,
          specialistId,
          specialistInfo: {
            specialistName,
            socialSecurityNo,
            specialistId,
          },
          editInfo: {
            isEditMode: true,
            clientAppointmentSpaceID: item.clientAppointmentSpaceID,
            startDate: new Date(item.startDate),
            startDateHour: moment(item.startDate).format('hh'),
            startDateMinute: moment(item.startDate).format('mm'),
            startDateAmPm: moment(item.startDate)
              .format('a')
              .toLowerCase(),
            endDate: new Date(item.endDate),
            endDateHour: moment(item.endDate).format('hh'),
            endDateMinute: moment(item.endDate).format('mm'),
            endDateAmPm: moment(item.endDate)
              .format('a')
              .toLowerCase(),
            serviceId: item.serviceId,
            company: {
              id: item.companyId,
              name: item.companyName,
            },
            municipality: {
              id: item.municipalityId,
              name: item.municipalityName,
            },
            serviceLocation: {
              locationId: item.locationId,
              locationName: item.locationName,
              specialistLocationId: item.specialistLocationId,
            },
          },
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .c-callout .c-content {
  @media only screen and (max-width: 600px) {
    background-color: white;
  }
}

.c-filterManager {
  @media only screen and (max-width: 600px) {
    ::v-deep .ab-btn.success {
      margin-right: 20px;
      width: 86%;
    }
  }
}
</style>
